import React, { FC } from 'react';
import { LinkTarget } from '@atlassian/learning-components';
import { useNavigate } from 'react-router-dom';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { ResourceCard } from 'src/components/ResourceCard';
import { formatDurationFromMinutes, getProductTag } from 'src/utils/resourceCardUtils';
import { ContentType } from 'src/types';
import { ATLENZ_FF, CollectionType } from 'src/constants';
import { getContentProduct } from 'src/utils/getContentProduct';
import { ResourceWithDurationAndType } from 'src/pages/CatalogPage/utils/taxonomy';
import { handleNavigate } from 'src/pages/CatalogPage/utils/handleNavigate';
import { getSlug } from 'src/utils/slug';
import { getCollectionUrl, getCertificationUrl, getUrlPrefix, stripLeadingSlash } from 'src/utils/getUrl';
import { AtlenzCatalogResource, AtlenzContentType } from 'src/services/http/search';
import { getResourceContentType, getResourceProduct } from 'src/pages/CatalogPage/utils/cardListUtils';
import { AdobeLearningObject } from 'src/services/http/adobe';

const OPTIONS = {
  [ContentType.LESSON]: { label: 'lesson', pathParam: 'lesson', target: '_self' },
  [ContentType.COURSE]: { label: 'course', pathParam: 'course', target: '_self' },
  [ContentType.LEARNING_PATH]: { label: 'learning path', pathParam: 'path', target: '_self' },
  [ContentType.CERTIFICATION]: {
    label: 'certification',
    pathParam: stripLeadingSlash(getCertificationUrl()),
    target: '_self',
  },
  [ContentType.ROLE]: {
    label: 'collection',
    pathParam: stripLeadingSlash(getCollectionUrl(CollectionType.ROLE_BASED)),
    target: '_self',
  },
  [ContentType.TOPIC]: {
    label: 'collection',
    pathParam: stripLeadingSlash(getCollectionUrl(CollectionType.TOPIC)),
    target: '_self',
  },
  [ContentType.CERTIFICATION_PREP]: {
    label: 'collection',
    pathParam: stripLeadingSlash(getCollectionUrl(CollectionType.CERT_PREP)),
    target: '_self',
  },
  // TODO:  replace ContentType.INSTRUCTOR_LED_TRAINING with the actual path param when contentful is updated

  // [ContentType.INSTRUCTOR_LED_TRAINING]: {
  //   label: 'team training',
  //   pathParam: 'team-training',
  //   target: '_self',
  // },
  [ContentType.INSTRUCTOR_LED_TRAINING]: { label: 'team training', pathParam: '', target: '_blank' },
};

type ExtractedContentType = Extract<
  ContentType,
  | ContentType.LESSON
  | ContentType.COURSE
  | ContentType.CERTIFICATION
  | ContentType.LEARNING_PATH
  | ContentType.CERTIFICATION_PREP
  | ContentType.INSTRUCTOR_LED_TRAINING
>;

const ATLENZ_OPTIONS = {
  [AtlenzContentType.lesson]: { label: 'lesson', pathParam: '', target: '_self' },
  [AtlenzContentType.course]: { label: 'course', pathParam: '', target: '_self' },
  [AtlenzContentType.learning_path]: { label: 'learning path', pathParam: '', target: '_self' },
  [AtlenzContentType.certification]: {
    label: 'certification',
    pathParam: '',
    target: '_self',
  },
  [AtlenzContentType.collection]: {
    label: 'collection',
    pathParam: '',
    target: '_self',
  },
  [AtlenzContentType.team_training]: { label: 'team training', pathParam: '', target: '_blank' },
};

const ADOBE_OPTIONS: Record<AdobeLearningObject['type'], { label: string; pathParam: string; target: string }> = {
  lesson: { label: 'lesson', pathParam: '', target: '_blank' },
  course: { label: 'course', pathParam: '', target: '_blank' },
  learningPath: { label: 'learning path', pathParam: '', target: '_blank' },
  certification: { label: 'certification', pathParam: '', target: '_blank' },
};

export const COLLECTION_TYPES = [ContentType.ROLE, ContentType.TOPIC, ContentType.CERTIFICATION_PREP];

interface CardsListProps {
  resources: ResourceWithDurationAndType[] | AtlenzCatalogResource[] | AdobeLearningObject[];
  handleCardClick: (slug: string, label: string) => void;
  showDescription?: boolean;
}

export const CardsList: FC<CardsListProps> = ({ resources, handleCardClick, showDescription = false }) => {
  const navigate = useNavigate();
  const isAtlenzAvailable = FeatureGates.checkGate(ATLENZ_FF);

  return (
    <>
      {resources.map((resource, index) => {
        const isAdobeResource = 'id' in resource && typeof resource.id === 'string' && !('contentAri' in resource);
        const isAtlenzResource = 'contentAri' in resource;

        let label: string, pathParam: string, target: string;

        if (isAdobeResource) {
          const adobeResource = resource as AdobeLearningObject;
          const options = ADOBE_OPTIONS[adobeResource.type];

          ({ label, pathParam, target } = options);
        } else if (isAtlenzResource || isAtlenzAvailable) {
          const atlenzResource = resource as AtlenzCatalogResource;
          const options = ATLENZ_OPTIONS[atlenzResource.type as AtlenzContentType];

          ({ label, pathParam, target } = options);
        } else {
          const standardResource = resource as ResourceWithDurationAndType;
          const options = OPTIONS[standardResource.__typename as ExtractedContentType];

          ({ label, pathParam, target } = options);
        }

        const resourceSlug = 'url' in resource ? resource.url : getSlug(resource);
        const slug = pathParam ? `/${pathParam}/${resourceSlug}` : `${resourceSlug}`;

        let product: string[], productTag: Array<{ label: string }>;

        if (isAdobeResource) {
          product = [];
          productTag = [];
        } else {
          product = getResourceProduct(resource as ResourceWithDurationAndType | AtlenzCatalogResource);
          productTag = getProductTag(product);
        }

        const tags = [
          ...(resource.duration ? [{ label: formatDurationFromMinutes(resource.duration) }] : []),
          { label },
          ...productTag,
        ];

        return (
          <ResourceCard
            contentType={getResourceContentType(resource as ResourceWithDurationAndType | AtlenzCatalogResource)}
            product={getContentProduct(product)}
            title={resource.title}
            link={pathParam ? `${getUrlPrefix()}${slug}` : slug}
            onClick={() => {
              handleCardClick(resourceSlug, label);
              handleNavigate(navigate, slug, target as LinkTarget, isAtlenzAvailable);
            }}
            target={target as LinkTarget}
            tags={tags}
            key={resourceSlug + index}
            {...(showDescription && resource.description ? { description: resource.description } : {})}
          />
        );
      })}
    </>
  );
};
